import { v4 as uuidv4 } from 'uuid';
import { moveElementInArray } from './global';
import { FreeformScene } from './FreeformScene';
import { GuidedScene } from './GuidedScene';
import { SoundCard, type CardKind, CardKinds, PlaySettings } from './SoundCard';

export type SceneKind = string;
export const SceneKinds = {
    Freeform: "freeform",
    Guided: "guided",
}


export interface Scene {
    id: string;
    name: string;
    kind: SceneKind;
    permalinkId: string;
    DeleteCard: (cardId: string) => void;
    GetCard: (cardId: string) => SoundCard;
    AddCard: (title: string, sound: any, kind: CardKind) => void;
    ContainsSound: (soundId: string) => boolean;
    RehashIds: () => void;
    RehashPermalinkIds: () => void;
    Permalink: () => string;
    AllCards: () => SoundCard[];
}


export class Soundboard {
    // Legacy Properties
    // TODO BL: remove when nobody in prod is using lanes anymore.
    lanes: any
    lane_coord_map: any
    card_coord_map: any

    scenes: Array<Scene>
    scene_indices: Map<string, number>

    constructor(rawData) {
        this.lanes = rawData["lanes"];
        this.card_coord_map = rawData["card_coord_map"];
        this.lane_coord_map = rawData["lane_coord_map"];

        if (rawData.scenes) {
            this.scenes = rawData.scenes.map((rawScene) => {
                let scene = undefined;
                if (!rawScene.kind) {
                    scene = new FreeformScene(rawScene);
                }

                if (rawScene.kind === SceneKinds.Freeform) {
                    scene = new FreeformScene(rawScene);
                } else if (rawScene.kind === SceneKinds.Guided) {
                    scene = new GuidedScene(rawScene);
                }

                // Sometimes, the state can get corrupted if two cards end up with the same ID (e.g. if we crash during a drag and drop)
                // In those cases, the application might enter an unrecoverable state because duplicate IDs create infinite loops.
                // So, we just rehash every ID on creation to make sure this doesn't happen.
                // We may end up with duplicate cards, but that's better than the app just not working because of a corrupted board.
                scene.RehashIds();
                return scene;
            })
            this.scene_indices = new Map(this.scenes.map((scene, idx) => [scene.id, idx]))
        } else if (rawData.lanes) {
            this.scenes = rawData.lanes.map((rawScene) => {
                return new FreeformScene(rawScene);
            })
            this.scene_indices = new Map(rawData.lanes.map((rawLane, idx) => [rawLane.id, idx]))
        } else {
            this.scenes = [];
            this.scene_indices = new Map();
        }
    }

    async store() {
        return fetch("/app/soundboard/board/update", {
            method: "POST",
            body: JSON.stringify(this),
            headers: {
                "Content-type": "application/json, charset=UTF-8",
            }
        })
            .then((response) => {
                if (!response.ok) {
                    // TODO BL: We probably want to:
                    // - Retry saving if possible
                    // - If not, get a JSON from the server and return board to that state.
                    // - If not, store the last saved state and return to that.
                    // In any case, we should show an alert that we failed to save state
                    return new Error("Something went wrong while saving. Returning board to unsaved state...")
                }
            })
    }

    getScene(sceneId: string): Scene {
        const idx = this.scene_indices.get(sceneId)
        return this.scenes[idx]
    }

    ContainsSound(soundId: string): boolean {
        return this.scenes.findIndex((scene) => scene.ContainsSound(soundId)) !== -1;
    }

    // TODO BL: This needs a pass for error handling, especially for OutOfBouds errors
    async DeleteCard(sceneId: string, cardId: string) {
        const scene = this.getScene(sceneId)
        scene.DeleteCard(cardId)
    }


    // TODO BL:  This needs to be inlined into the particular scenes
    async AddCardToLane(laneId, title, sound) {
        const scene = this.getScene(laneId)
        scene.AddCard(title, sound, CardKinds.Plain)
    }

    async AddScene(kind: SceneKind) {
        const id = uuidv4();
        const idx = this.scenes.length;

        let scene = null;
        if (kind === SceneKinds.Freeform) {
            scene = new FreeformScene(
                {
                    id: id,
                    name: `Scene ${idx} (double click to edit)`,
                    cards: [],
                }
            );
        } else {
            scene = new GuidedScene({
                id: id,
                name: `Scene ${idx} (double click to edit)`,
            });
        }

        this.scenes.push(scene);
        this.scene_indices.set(id, idx);
    }

    async AddExistingRawScene(rawScene: any, atIdx: number) {
        let scene = null;
        if (rawScene.kind === SceneKinds.Freeform) {
            scene = new FreeformScene(rawScene);
        } else {
            scene = new GuidedScene(rawScene);
        }
        scene.RehashIds();
        scene.RehashPermalinkIds();

        const idx = this.scenes.length;
        this.scenes.push(scene);
        this.scene_indices.set(scene.id, idx);
        this.MoveScene(idx, atIdx);
    }

    async RenameCard(sceneId: string, cardId: string, newName: string) {
        const scene = this.getScene(sceneId);
        const card = scene.GetCard(cardId);
        card.name = newName;
    }

    async ChangeCardPlaySettings(sceneId: string, cardId: string, mutator: (PlaySettings) => PlaySettings) {
        const scene = this.getScene(sceneId);
        const card = scene.GetCard(cardId);
        card.playSettings = mutator(card.playSettings);
    }

    async RenameScene(sceneId: string, newName: string) {
        const scene = this.getScene(sceneId)
        scene.name = newName;
    }

    async MoveScene(oldIdx, newIdx) {
        moveElementInArray(this.scenes, oldIdx, newIdx);
        this.scenes.forEach((scene, sceneIdx) => {
            const sceneId = scene.id;
            this.scene_indices.set(sceneId, sceneIdx);
        })
    }

    async DeleteScene(sceneId: string) {
        const idx = this.scene_indices.get(sceneId);
        this.scenes.splice(idx, 1);
        this.scenes.forEach((scene, sceneIdx) => {
            const sceneId = scene.id;
            this.scene_indices.set(sceneId, sceneIdx);
        })
    }
}